import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Photo } from '@/components/ui/Photo';
import { Reveal } from '@/components/ui/Reveal';
import { useSite } from '@/features/site/SiteProvider';

/** Section D — private label / OEM journey (steps come from the CMS "process" content). */
export function PrivateLabel() {
  const { process } = useSite().content;

  return (
    <section aria-labelledby="private-label-title" className="section-y bg-white">
      <div className="container-page">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow flex items-center gap-3 text-accent-dark">
              <span aria-hidden className="h-px w-8 bg-accent" />
              {process.eyebrow}
            </p>
            <h2 id="private-label-title" className="display-lg mt-4 text-primary">
              {process.heading}{' '}
              {process.headingMuted && <span className="editorial-italic block text-muted">{process.headingMuted}</span>}
            </h2>
            {process.intro && <p className="mt-6 text-[16.5px] leading-[1.7] text-muted">{process.intro}</p>}
            <div className="mt-9 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
              <ButtonLink to="/private-label#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
                Discuss Your Project
              </ButtonLink>
              <ButtonLink to="/private-label" variant="outline" size="lg">
                How it works
              </ButtonLink>
            </div>
          </div>

          <div className="relative lg:col-span-7">
            <div className="aspect-[16/11] overflow-hidden rounded-[8px] bg-surface">
              <Photo name="blankTag" sizes="(min-width: 1024px) 55vw, 100vw" position="50% 40%" />
            </div>
            <p className="absolute bottom-5 left-5 max-w-[260px] rounded-[6px] bg-white/95 px-4 py-3 text-[13.5px] leading-snug text-ink-soft shadow-[var(--shadow-card)]">
              <span className="font-semibold text-primary">Your label, your packaging.</span> No stock lots — only made-to-order production for your brand.
            </p>
          </div>
        </div>

        <ol className="mt-16 grid gap-px overflow-hidden rounded-[8px] border border-line bg-line sm:grid-cols-2 lg:mt-20 lg:grid-cols-4">
          {process.steps.map((step, i) => (
            <Reveal as="li" key={step.title} delay={i * 80} className="flex flex-col bg-white p-7">
              <div className="flex items-center justify-between">
                <span className="font-display text-[34px] leading-none font-light text-muted tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <span className="grid h-10 w-10 place-items-center rounded-[6px] bg-accent-soft text-accent-dark">
                  <Icon name={step.icon} className="h-[18px] w-[18px]" />
                </span>
              </div>
              <h3 className="mt-6 font-display text-[19px] font-bold tracking-tight text-primary">{step.title}</h3>
              <p className="mt-2.5 text-[14.5px] leading-[1.65] text-muted">{step.summary}</p>
              {step.points.length > 0 && (
                <ul className="mt-5 space-y-1.5 border-t border-line pt-4">
                  {step.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-[13.5px] text-ink-soft">
                      <span aria-hidden className="h-1 w-1 rounded-full bg-accent" />
                      {p}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}
